import React, { useRef } from "react";
import { GridCellProps } from "@progress/kendo-react-grid";
import type { LegalEntitySelectItem } from "../types";

interface LegalEntityCellProps extends Omit<GridCellProps, "onChange"> {
  legalEntities: LegalEntitySelectItem[];
  isLoading?: boolean;
  /** DNC-390: true for the Agent role, who may not reassign the legal entity. */
  disabled?: boolean;
  onChange: (phoneNumber: string, legalEntityId: string, revert: () => void) => void;
}

/**
 * Legal entity inline dropdown — exact mirror of ServiceTypeCell pattern:
 * no useState/useEffect, controlled value from dataItem, ref for revert.
 */
const LegalEntityCell: React.FC<LegalEntityCellProps> = (props) => {
  const dataItem = props.dataItem;
  const currentValue = dataItem.legalEntityId ?? "";
  const selectRef = useRef<HTMLSelectElement>(null);
  const prevValueRef = useRef<string>(currentValue);

  const handleFocus = () => {
    prevValueRef.current = selectRef.current?.value ?? currentValue;
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    const revert = () => {
      if (selectRef.current) {
        selectRef.current.value = prevValueRef.current;
      }
    };
    props.onChange(dataItem.phoneNumber, newValue, revert);
  };

  return (
    <td>
      <div style={{ position: "relative" }}>
        <select
          ref={selectRef}
          className="form-select form-select-sm"
          value={currentValue}
          onFocus={handleFocus}
          onChange={handleChange}
          disabled={props.isLoading || props.disabled}
          style={props.isLoading ? { opacity: 0.5 } : undefined}
        >
          {props.legalEntities.map((le) => (
            <option key={le.legalEntityId} value={String(le.legalEntityId)}>
              {le.legalName}
            </option>
          ))}
        </select>
        {props.isLoading && (
          <div style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <div className="spinner-border spinner-border-sm" role="status" style={{ width: 16, height: 16 }} />
          </div>
        )}
      </div>
    </td>
  );
};

export default LegalEntityCell;
